import { useEffect, useRef } from "react"

import { getUploadedFilesCountSelector, getUploadingFilesCountSelector } from "../selectors/filesUploadingSelectors"

/**
 * Callback function type for upload finish detection
 */
export type UploadFinishCallback = () => void

/**
 * Custom hook to detect when file uploading is finished
 * Calls the provided callback when all uploading files are completed
 * @param callback - Function to call when uploading is finished
 */
export const useUploadingFinishDetection = (callback: UploadFinishCallback): void => {
  const uploadingFilesCount = getUploadingFilesCountSelector()
  const uploadedFilesCount = getUploadedFilesCountSelector()

  const isFirstCallRef = useRef(true)
  const callbackRef = useRef<UploadFinishCallback>(() => undefined)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    if (isFirstCallRef.current) {
      isFirstCallRef.current = false
      return
    }

    // Check if all uploading files are completed
    if (uploadingFilesCount > 0 && uploadedFilesCount > 0 && uploadingFilesCount === uploadedFilesCount) {
      callbackRef.current()
    }
  }, [uploadingFilesCount, uploadedFilesCount])
}
